/**
 * NavThemePicker Component
 * Compact theme picker designed to fit in navigation bars
 * Shows current color swatch with dropdown for selection
 */

import React, { useRef, useEffect, useState } from 'react';
import { Palette, Check } from 'lucide-react';
import { useAppTheme, ColorPalette } from '../../context/AppThemeContext';

export const NavThemePicker: React.FC = () => {
  const { currentPalette, palettes, setPalette } = useAppTheme();
  const [isOpen, setIsOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    };

    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [isOpen]);

  // Close on escape
  useEffect(() => {
    if (!isOpen) return;

    const handleEscape = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        setIsOpen(false);
      }
    };

    document.addEventListener('keydown', handleEscape);
    return () => document.removeEventListener('keydown', handleEscape);
  }, [isOpen]);

  const handleSelectPalette = (paletteId: string) => {
    setPalette(paletteId);
    setIsOpen(false);
  };

  return (
    <div ref={dropdownRef} className="relative">
      {/* Trigger Button */}
      <button
        onClick={() => setIsOpen(!isOpen)}
        className="flex items-center gap-2 px-3 py-2 rounded-lg hover:bg-stone-100 transition-colors group"
        aria-label="Change color theme"
        aria-expanded={isOpen}
      >
        <Palette className="w-4 h-4 text-stone-500 group-hover:text-stone-700" />
        <div
          className="w-5 h-5 rounded-full border-2 border-white shadow-sm ring-1 ring-stone-200"
          style={{ backgroundColor: currentPalette.preview }}
        />
      </button>

      {/* Dropdown */}
      {isOpen && (
        <div className="absolute right-0 top-full mt-2 w-56 bg-white rounded-xl shadow-xl border border-stone-200 overflow-hidden z-50 animate-fade-in">
          {/* Header */}
          <div className="px-4 py-3 border-b border-stone-100 bg-stone-50">
            <p className="text-sm font-medium text-stone-900">Color Theme</p>
            <p className="text-xs text-stone-500">Choose your preferred colors</p>
          </div>

          {/* Palette Options */}
          <div className="p-2 max-h-72 overflow-y-auto">
            {palettes.map((palette) => (
              <PaletteOption
                key={palette.id}
                palette={palette}
                isSelected={currentPalette.id === palette.id}
                onSelect={() => handleSelectPalette(palette.id)}
              />
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

interface PaletteOptionProps {
  palette: ColorPalette;
  isSelected: boolean;
  onSelect: () => void;
}

const PaletteOption: React.FC<PaletteOptionProps> = ({ palette, isSelected, onSelect }) => {
  return (
    <button
      onClick={onSelect}
      className={`w-full flex items-center gap-3 px-3 py-2 rounded-lg transition-all duration-150 ${
        isSelected
          ? 'bg-stone-100'
          : 'hover:bg-stone-50'
      }`}
    >
      {/* Color Swatches */}
      <div className="flex -space-x-1">
        {[palette.colors.primary[400], palette.colors.primary[500], palette.colors.primary[600]].map(
          (color, i) => (
            <div
              key={i}
              className="w-5 h-5 rounded-full border-2 border-white shadow-sm"
              style={{ backgroundColor: color, zIndex: 3 - i }}
            />
          )
        )}
      </div>

      {/* Label */}
      <div className="flex-1 text-left">
        <span className="text-sm font-medium text-stone-800">{palette.name}</span>
      </div>

      {/* Check */}
      {isSelected && (
        <Check className="w-4 h-4 text-stone-600" />
      )}
    </button>
  );
};
